import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import './WebsiteManagement.css';
import './WebsiteManagement-Mobile.css';
import { uploadService } from '../../services/uploadService';
import { agentWebsiteService, contentService, websiteContactService } from '../../services';
import agentWebsiteProfileService from '../../services/agentWebsiteProfileService';
import agentContentSelectionsService from '../../services/agentContentSelectionsService';
import promotionsService from '../../services/promotionsService';
import RichTextEditor from '../common/RichTextEditor';
import WebsiteSubscriptionGuard from './WebsiteSubscriptionGuard';
import './WebsiteManagement.css';

const WebsiteManagement = () => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('content');
  const [contentCards, setContentCards] = useState([]);
  const [blogPosts, setBlogPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showBlogModal, setShowBlogModal] = useState(false);
  const [editingBlog, setEditingBlog] = useState(null);
  
  // Website configuration state
  const [websiteConfig, setWebsiteConfig] = useState(null);
  const [availableThemes, setAvailableThemes] = useState([]);
  const [configForm, setConfigForm] = useState({
    subdomain: '',
    custom_domain: '',
    theme_id: '',
    meta_title: '',
    meta_description: '',
    google_analytics_id: '',
    google_search_console_code: '',
    facebook_pixel_id: ''
  });
  const [logoFile, setLogoFile] = useState(null);
  const [logoPreview, setLogoPreview] = useState(null);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);

  const [agentSettings, setAgentSettings] = useState({
    selectedContent: []
  });

  // Website profile state
  const [websiteProfile, setWebsiteProfile] = useState({
    display_name: '',
    agency_name: '',
    public_email: '',
    public_phone: '',
    title: '',
    bio: '',
    years_experience: '',
    specialties: '',
    agency_address: '',
    agency_website: '',
    linkedin_url: '',
    facebook_url: '',
    instagram_url: '',
    tiktok_url: '',
    photo_url: ''
  });
  const [profileSaving, setProfileSaving] = useState(false);
  const [profilePhotoFile, setProfilePhotoFile] = useState(null);
  const [profilePhotoPreview, setProfilePhotoPreview] = useState(null);


  // Navigation/Categories state
  const [websiteCategories, setWebsiteCategories] = useState([]);
  const [selectedNavCategories, setSelectedNavCategories] = useState([]);
  const [categoriesSaving, setCategoriesSaving] = useState(false);
  const [availableCategories, setAvailableCategories] = useState([]);
  const [selectedContentCards, setSelectedContentCards] = useState([]);
  const [contentSelectionsSaving, setContentSelectionsSaving] = useState(false);

  // Offers state
  const [offers, setOffers] = useState([]);
  const [subcategories, setSubcategories] = useState([]);
  const [showOfferModal, setShowOfferModal] = useState(false);
  const [editingOffer, setEditingOffer] = useState(null);
  const [offersSaving, setOffersSaving] = useState(false);

  // Promotions state
  const [promotions, setPromotions] = useState([]);
  const [showPromotionModal, setShowPromotionModal] = useState(false);
  const [editingPromotion, setEditingPromotion] = useState(null);

  // Services state
  const [services, setServices] = useState([]);
  const [servicesDescription, setServicesDescription] = useState('');
  const [servicesSaving, setServicesSaving] = useState(false);
  const [showServiceModal, setShowServiceModal] = useState(false);
  const [editingService, setEditingService] = useState(null);
  const [newService, setNewService] = useState({
    serviceName: '',
    serviceDescription: ''
  });

  // Submissions state
  const [submissions, setSubmissions] = useState([]);
  const [submissionsLoading, setSubmissionsLoading] = useState(false);
  const [submissionFilters, setSubmissionFilters] = useState({
    status: 'all',
    type: 'all'
  });

  const categories = [
    'Cruise', 'Theme Park', 'Hotel', 'Resort', 
    'All-Inclusive', 'Transportation', 'Flight', 'Tour/Activity'
  ];

  // Helper function to safely format dates
  const formatDate = (dateString) => {
    if (!dateString) return 'No Date';
    try {
      // Handle both ISO strings and date-only strings
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return 'No Date';
      return date.toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric'
      });
    } catch (error) {
      console.error('Date formatting error:', error);
      return 'No Date';
    }
  };

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      setLoading(true);
      const [contentResponse, blogResponse, websiteResponse, profileResponse, categoriesResponse, offersResponse, selectionsResponse, servicesResponse] = await Promise.all([
        contentService.cards.getAll(),
        contentService.blog.getAll({ agent_id: user.id }),
        agentWebsiteService.getConfig(),
        agentWebsiteProfileService.getProfile(),
        agentWebsiteService.getCategories(),
        agentWebsiteService.getOffers(),
        // Load existing content card selections
        agentContentSelectionsService.getSelections(),
        // Load services data
        agentWebsiteService.getServices()
      ]);

      setContentCards(contentResponse.data || []);
      setPromotions(contentResponse.data || []); // Promotions are the same as content cards
      setBlogPosts(blogResponse.data || []);
      
      // Set website configuration
      if (websiteResponse.website) {
        setWebsiteConfig(websiteResponse.website);
        setConfigForm({
          subdomain: websiteResponse.website.subdomain || '',
          custom_domain: websiteResponse.website.custom_domain || '',
          theme_id: websiteResponse.website.theme_id || '',
          meta_title: websiteResponse.website.meta_title || '',
          meta_description: websiteResponse.website.meta_description || '',
          google_analytics_id: websiteResponse.website.google_analytics_id || '',
          google_search_console_code: websiteResponse.website.google_search_console_code || '',
          facebook_pixel_id: websiteResponse.website.facebook_pixel_id || ''
        });
      }
      
      setAvailableThemes(websiteResponse.themes || []);
      
      // Set website profile
      if (profileResponse) {
        setWebsiteProfile({
          display_name: profileResponse.display_name || '',
          agency_name: profileResponse.agency_name || '',
          public_email: profileResponse.public_email || '',
          public_phone: profileResponse.public_phone || '',
          title: profileResponse.title || '',
          bio: profileResponse.bio || '',
          years_experience: profileResponse.years_experience || '',
          specialties: profileResponse.specialties || '',
          agency_address: profileResponse.agency_address || '',
          agency_website: profileResponse.agency_website || '',
          linkedin_url: profileResponse.linkedin_url || '',
          facebook_url: profileResponse.facebook_url || '',
          instagram_url: profileResponse.instagram_url || '',
          tiktok_url: profileResponse.tiktok_url || '',
          photo_url: profileResponse.photo_url || ''
        });
      }
      
      
      // Set categories
      setWebsiteCategories(categoriesResponse?.available || []);
      setSelectedNavCategories(categoriesResponse?.selected || []);
      
      // Set offers
      setOffers(offersResponse?.offers || []);
      setSubcategories(offersResponse?.subcategories || []);
      
      // Set services data
      setServices(servicesResponse?.services || []);
      setServicesDescription(servicesResponse?.servicesDescription || '');
      
      // Set available categories from agent website categories
      setAvailableCategories(categoriesResponse?.available || []);
      
      // Process existing selections and mark selected cards
      const selectedCardIds = selectionsResponse?.map(selection => selection.content_card_id) || [];
      setSelectedContentCards(selectedCardIds);
      
      // Mark content cards as selected based on existing selections
      if (contentResponse.data && selectedCardIds.length > 0) {
        const updatedCards = contentResponse.data.map(card => ({
          ...card,
          selected: selectedCardIds.includes(card.id)
        }));
        setContentCards(updatedCards);
      }

    } catch (error) {
      console.error('Error loading website data:', error);
      setError('Failed to load website data. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Submissions functions
  const fetchSubmissions = async () => {
    try {
      setSubmissionsLoading(true);
      const response = await websiteContactService.getSubmissions(submissionFilters);
      setSubmissions(response.data || []);
    } catch (error) {
      console.error('Error fetching submissions:', error);
      setError('Failed to load submissions');
    } finally {
      setSubmissionsLoading(false);
    }
  };

  const handleStatusUpdate = async (submissionId, newStatus) => {
    try {
      await websiteContactService.updateStatus(submissionId, newStatus);
      
      // Update local state
      setSubmissions(submissions.map(sub => 
        sub.id === submissionId ? { ...sub, status: newStatus } : sub
      ));
      
      setSuccess('Status updated successfully');
    } catch (error) {
      console.error('Error updating status:', error);
      setError('Failed to update status');
    }
  };

  const handleViewDetails = (submission) => {
    // For now, just show an alert with details
    // Later this could open a modal
    const details = `
Name: ${submission.name}
Email: ${submission.email}
Type: ${submission.purpose === 'quote' ? 'Quote Request' : 'General Contact'}
Date: ${new Date(submission.submitted_at).toLocaleString()}
Status: ${submission.status}

Message:
${submission.message}
    `;
    
    alert(details);
  };

  // Add useEffect for submissions
  useEffect(() => {
    if (activeTab === 'submissions') {
      fetchSubmissions();
    }
  }, [activeTab, submissionFilters]);

  const handleBlogSubmit = async (blogData) => {
    try {
      if (editingBlog) {
        await contentService.blog.update(editingBlog.id, blogData);
      } else {
        await contentService.blog.create({
          ...blogData,
          status: 'published'
        });
      }
      
      setShowBlogModal(false);
      setEditingBlog(null);
      loadData();
      setSuccess('Blog post saved successfully!');
    } catch (error) {
      console.error('Error saving blog post:', error);
      setError('Failed to save blog post. Please try again.');
    }
  };

  const handleDeleteBlog = async (id) => {
    if (window.confirm('Are you sure you want to delete this blog post?')) {
      try {
        await contentService.blog.delete(id);
        loadData();
        setSuccess('Blog post deleted successfully!');
      } catch (error) {
        console.error('Error deleting blog post:', error);
        setError('Failed to delete blog post. Please try again.');
      }
    }
  };


  const handleContentCardToggle = (cardId) => {
    const updatedCards = contentCards.map(card => 
      card.id === cardId ? { ...card, selected: !card.selected } : card
    );
    setContentCards(updatedCards);
    
    // Update selected content cards list
    const newSelectedCards = updatedCards
      .filter(card => card.selected)
      .map(card => card.id);
    setSelectedContentCards(newSelectedCards);
  };

  const handleCategorySectionToggle = (category) => {
    const isCurrentlySelected = selectedNavCategories.some(cat => cat.category_id === category.id);
    
    if (isCurrentlySelected) {
      // Remove category
      setSelectedNavCategories(prev => 
        prev.filter(cat => cat.category_id !== category.id)
      );
    } else {
      // Add category
      const newCategory = {
        category_id: category.id,
        display_order: selectedNavCategories.length + 1,
        custom_title: category.name,
        is_enabled: true
      };
      setSelectedNavCategories(prev => [...prev, newCategory]);
    }
  };

  const saveCategorySelections = async () => {
    try {
      setCategoriesSaving(true);
      await agentWebsiteService.updateCategories({ selectedCategories: selectedNavCategories });
      setSuccess('Category pages updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving category selections:', error);
      setError('Failed to save category selections. Please try again.');
      setTimeout(() => setError(null), 5000);
    } finally {
      setCategoriesSaving(false);
    }
  };

  const saveContentSelections = async () => {
    try {
      setContentSelectionsSaving(true);
      const selectedCardIds = contentCards
        .filter(card => card.selected)
        .map(card => card.id);
      
      await agentContentSelectionsService.saveSelections(selectedCardIds);
      setSuccess('Content selections saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving content selections:', error);
      setError('Failed to save content selections. Please try again.');
    } finally {
      setContentSelectionsSaving(false);
    }
  };

  const handleSaveSettings = async () => {
    try {
      setSaving(true);
      
      // Save website configuration
      await agentWebsiteService.updateConfig(configForm);
      
      // Upload logo if provided
      if (logoFile) {
        await agentWebsiteService.uploadLogo(logoFile);
      }
      
      
      setSuccess('Website settings saved successfully!');
      loadData(); // Reload to get updated data
    } catch (error) {
      console.error('Error saving website settings:', error);
      setError('Failed to save website settings. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  const handleSaveProfile = async () => {
    try {
      setProfileSaving(true);
      
      let updatedProfile = { ...websiteProfile };
      
      // Handle photo upload if a new photo was selected
      if (profilePhotoFile) {
        try {
          const formData = new FormData();
          formData.append('photo', profilePhotoFile);
          
          const uploadResponse = await agentWebsiteProfileService.uploadPhoto(formData);
          updatedProfile.photo_url = uploadResponse.photo_url;
          
          // Update local state
          setWebsiteProfile(updatedProfile);
          setProfilePhotoFile(null);
          setProfilePhotoPreview(null);
        } catch (uploadError) {
          console.error('Error uploading photo:', uploadError);
          setError('Failed to upload photo. Profile saved without photo.');
        }
      }
      
      await agentWebsiteProfileService.updateProfile(updatedProfile);
      setSuccess('Profile updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving profile:', error);
      setError('Failed to save profile. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setProfileSaving(false);
    }
  };

  // Services management handlers
  const handleSaveServicesDescription = async () => {
    try {
      setServicesSaving(true);
      await agentWebsiteService.updateServicesDescription(servicesDescription);
      setSuccess('Services description updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving services description:', error);
      setError('Failed to save services description. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setServicesSaving(false);
    }
  };

  const handleAddService = async () => {
    if (!newService.serviceName.trim()) {
      setError('Service name is required.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setServicesSaving(true);
      await agentWebsiteService.createService({
        serviceName: newService.serviceName.trim(),
        serviceDescription: newService.serviceDescription.trim()
      });
      
      // Reload services data
      const servicesResponse = await agentWebsiteService.getServices();
      setServices(servicesResponse?.services || []);
      
      // Reset form
      setNewService({ serviceName: '', serviceDescription: '' });
      setSuccess('Service added successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error adding service:', error);
      setError('Failed to add service. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setServicesSaving(false);
    }
  };

  const handleEditService = (service) => {
    setEditingService(service);
    setNewService({
      serviceName: service.service_name,
      serviceDescription: service.service_description || ''
    });
    setShowServiceModal(true);
  };

  const handleUpdateService = async () => {
    if (!newService.serviceName.trim()) {
      setError('Service name is required.');
      setTimeout(() => setError(null), 3000);
      return;
    }

    try {
      setServicesSaving(true);
      await agentWebsiteService.updateService(editingService.id, {
        serviceName: newService.serviceName.trim(),
        serviceDescription: newService.serviceDescription.trim(),
        isEnabled: true
      });
      
      // Reload services data
      const servicesResponse = await agentWebsiteService.getServices();
      setServices(servicesResponse?.services || []);
      
      // Reset form and close modal
      setNewService({ serviceName: '', serviceDescription: '' });
      setEditingService(null);
      setShowServiceModal(false);
      setSuccess('Service updated successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error updating service:', error);
      setError('Failed to update service. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setServicesSaving(false);
    }
  };

  const handleDeleteService = async (serviceId) => {
    if (!window.confirm('Are you sure you want to delete this service?')) {
      return;
    }

    try {
      setServicesSaving(true);
      await agentWebsiteService.deleteService(serviceId);
      
      // Reload services data
      const servicesResponse = await agentWebsiteService.getServices();
      setServices(servicesResponse?.services || []);
      
      setSuccess('Service deleted successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error deleting service:', error);
      setError('Failed to delete service. Please try again.');
      setTimeout(() => setError(null), 3000);
    } finally {
      setServicesSaving(false);
    }
  };

  // Promotion handlers
  const handleSavePromotion = async (promotionData) => {
    try {
      if (editingPromotion) {
        // Update existing promotion
        await contentService.cards.update(editingPromotion.id, promotionData);
      } else {
        // Create new promotion
        await contentService.cards.create(promotionData);
      }
      
      setShowPromotionModal(false);
      setEditingPromotion(null);
      loadData(); // Reload data to show changes
      setSuccess('Content card saved successfully!');
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error saving content card:', error);
      setError('Failed to save content card. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleTogglePromotion = async (promotionId, isActive) => {
    try {
      await contentService.cards.update(promotionId, { status: isActive ? 'active' : 'inactive' });
      loadData(); // Reload data to show changes
      setSuccess(`Content card ${isActive ? 'activated' : 'deactivated'} successfully!`);
      setTimeout(() => setSuccess(null), 3000);
    } catch (error) {
      console.error('Error toggling content card:', error);
      setError('Failed to update content card status. Please try again.');
      setTimeout(() => setError(null), 3000);
    }
  };

  const handleDeletePromotion = async (promotionId) => {
    if (window.confirm('Are you sure you want to delete this content card?')) {
      try {
        await contentService.cards.delete(promotionId);
        loadData(); // Reload data to show changes
        setSuccess('Content card deleted successfully!');
        setTimeout(() => setSuccess(null), 3000);
      } catch (error) {
        console.error('Error deleting content card:', error);
        setError('Failed to delete content card. Please try again.');
        setTimeout(() => setError(null), 3000);
      }
    }
  };

  if (loading) {
    return (
      <div className="website-loading">
        <div className="loading-spinner"></div>
        <p>Loading website management...</p>
      </div>
    );
  }

  return (
    <div className="website-management">
      {error && (
        <div className="error-message">
          <p>{error}</p>
          <button onClick={() => setError(null)}>Dismiss</button>
        </div>
      )}
      {success && (
        <div className="success-message">
          <p>{success}</p>
          <button onClick={() => setSuccess(null)}>Dismiss</button>
        </div>
      )}
      <div className="website-header">
        <h1>Website Management</h1>
        <p className="website-subtitle">Customize your agent website content and appearance</p>
      </div>

      {/* Navigation Tabs */}
      <div className="website-tabs">
        <button
          className={`website-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          Content Selection
        </button>
        <button
          className={`website-tab ${activeTab === 'promotions' ? 'active' : ''}`}
          onClick={() => setActiveTab('promotions')}
        >
          Content Cards
        </button>
        <button
          className={`website-tab ${activeTab === 'blog' ? 'active' : ''}`}
          onClick={() => setActiveTab('blog')}
        >
          Blog Posts
        </button>
        <button
          className={`website-tab ${activeTab === 'profile' ? 'active' : ''}`}
          onClick={() => setActiveTab('profile')}
        >
          Website Profile
        </button>
        <button
          className={`website-tab ${activeTab === 'settings' ? 'active' : ''}`}
          onClick={() => setActiveTab('settings')}
        >
          Website Settings
        </button>
        <button
          className={`website-tab ${activeTab === 'submissions' ? 'active' : ''}`}
          onClick={() => setActiveTab('submissions')}
        >
          Contact & Quotes
        </button>
      </div>

      {/* Content Selection Tab */}
      {activeTab === 'content' && (
        <div className="content-selection">
          <div className="content-section">
            <h2>Available Content</h2>
            <p className="section-description">Select promotional content to display on your website</p>
            <div className="content-cards-grid">
              {contentCards.map(card => (
                  <div key={card.id} className="content-card-item">
                    <label className="content-checkbox">
                      <input
                        type="checkbox"
                        checked={card.selected || false}
                        onChange={() => handleContentCardToggle(card.id)}
                      />
                      <div className="content-info">
                        <h3>{card.title}</h3>
                        <p>{card.short_description}</p>
                        {card.main_photo && (
                          <img 
                            src={card.main_photo} 
                            alt={card.title}
                            className="content-card-image"
                            style={{width: '100%', height: '120px', objectFit: 'cover', borderRadius: '4px', marginBottom: '8px'}}
                          />
                        )}
                        <div className="content-meta">
                          <span className="content-supplier">{card.supplier?.name || 'No Supplier'}</span>
                          <span className="content-category">
                            {card.categories?.map(cat => cat.name).join(', ') || 'No Category'}
                          </span>
                        </div>
                      </div>
                    </label>
                  </div>
                ))}
            </div>
          </div>

          {/* Travel Category Sections */}
          <div className="content-section">
            <h2>Travel Category Pages</h2>
            <p className="section-description">Activate travel categories to create dedicated pages under your Services menu</p>
            <div className="category-sections-grid">
              {availableCategories.map(category => (
                <div key={category.id} className="category-section-card">
                  <div className="category-section-header">
                    <div className="category-section-info">
                      <div className="category-icon">
                        {category.icon && <i className={category.icon} aria-hidden="true"></i>}
                      </div>
                      <div className="category-details">
                        <h3>{category.name}</h3>
                        <p>{category.description || `Showcase ${category.name.toLowerCase()} offerings to your clients`}</p>
                      </div>
                    </div>
                    <label className="category-section-toggle">
                      <input
                        type="checkbox"
                        checked={selectedNavCategories.some(cat => cat.category_id === category.id)}
                        onChange={() => handleCategorySectionToggle(category)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                  
                  {selectedNavCategories.some(cat => cat.category_id === category.id) && (
                    <div className="category-section-content">
                      <div className="content-cards-preview">
                        <h4>Content for this page:</h4>
                        <div className="category-cards-horizontal">
                          {contentCards
                            .filter(card => card.categories?.some(cat => cat.id === category.id))
                            .slice(0, 3)
                            .map(card => (
                              <div key={card.id} className="mini-content-card">
                                {card.main_photo && (
                                  <img 
                                    src={card.main_photo} 
                                    alt={card.title}
                                    className="mini-card-image"
                                  />
                                )}
                                <div className="mini-card-title">{card.title}</div>
                              </div>
                            ))}
                          {contentCards.filter(card => card.categories?.some(cat => cat.id === category.id)).length > 3 && (
                            <div className="mini-content-card more-cards">
                              +{contentCards.filter(card => card.categories?.some(cat => cat.id === category.id)).length - 3} more
                            </div>
                          )}
                          {contentCards.filter(card => card.categories?.some(cat => cat.id === category.id)).length === 0 && (
                            <div className="no-content-message">
                              No content cards available for this category yet
                            </div>
                          )}
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ))}
            </div>
          </div>

          <div className="settings-actions">
            <button 
              className="btn-save-settings" 
              onClick={saveContentSelections}
              disabled={contentSelectionsSaving}
            >
              {contentSelectionsSaving ? 'Saving Content...' : 'Save Content Selections'}
            </button>
            <button 
              className="btn-save-settings" 
              onClick={saveCategorySelections}
              disabled={categoriesSaving}
            >
              {categoriesSaving ? 'Saving Categories...' : 'Save Category Pages'}
            </button>
            <button className="btn-save-settings" onClick={handleSaveSettings}>
              Save Website Settings
            </button>
          </div>
        </div>
      )}

      {/* Blog Posts Tab */}
      {activeTab === 'blog' && (
        <div className="blog-management">
          <div className="blog-header">
            <h2>Blog Posts</h2>
            <button 
              className="btn-new-blog"
              onClick={() => {
                setEditingBlog(null);
                setShowBlogModal(true);
              }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Blog Post
            </button>
          </div>

          <div className="blog-posts-grid">
            {blogPosts.length === 0 ? (
              <div className="empty-state">
                <svg className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3>No blog posts yet</h3>
                <p>Create your first blog post to engage with clients</p>
              </div>
            ) : (
              blogPosts.map(post => (
                <div key={post.id} className="blog-post-card">
                  <div className="blog-post-image">
                    {post.files && post.files.length > 0 ? (
                      <img src={post.files[0].url || `/uploads/${post.files[0].path}`} alt={post.title} />
                    ) : (
                      <div className="blog-post-placeholder">
                        <svg fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 002 2v12a2 2 0 002 2z" />
                        </svg>
                      </div>
                    )}
                  </div>
                  <div className="blog-post-content">
                    <h3>{post.title}</h3>
                    <p className="blog-excerpt">{post.excerpt || post.content.substring(0, 150) + '...'}</p>
                    <div className="blog-meta">
                      <span className="blog-category">{post.category}</span>
                      <span className="blog-date">
                        {formatDate(post.publish_date || post.created_at)}
                      </span>
                    </div>
                    <div className="blog-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => {
                          setEditingBlog(post);
                          setShowBlogModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeleteBlog(post.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Website Settings Tab */}
      {activeTab === 'settings' && (
        <div className="website-settings">
          <div className="settings-header">
            <h2>Website Settings</h2>
          </div>

          <div className="settings-section">
            <h3>General Settings</h3>
            <div className="form-row">
              <div className="form-group">
                <label>Subdomain *</label>
                <div className="subdomain-input">
                  <input
                    type="text"
                    value={configForm.subdomain}
                    onChange={(e) => setConfigForm({ ...configForm, subdomain: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '') })}
                    placeholder="your-name"
                    required
                  />
                  <span>.tpcrm.io</span>
                </div>
                <small>Only lowercase letters, numbers, and hyphens allowed</small>
              </div>

              <div className="form-group">
                <label>Custom Domain (Optional)</label>
                <input
                  type="text"
                  value={configForm.custom_domain}
                  onChange={(e) => setConfigForm({ ...configForm, custom_domain: e.target.value.toLowerCase().trim() })}
                  placeholder="www.yourtravel.com"
                />
                <small>Use your own domain instead of the subdomain above. Leave blank to use subdomain.</small>
                
                {configForm.custom_domain && configForm.custom_domain.trim() && (
                  <div className="dns-instructions" style={{
                    marginTop: '15px',
                    padding: '15px',
                    backgroundColor: '#f8f9fa',
                    border: '1px solid #e9ecef',
                    borderRadius: '5px'
                  }}>
                    <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>DNS Configuration Required</h4>
                    <p style={{ margin: '0 0 10px 0', fontSize: '14px' }}>
                      To use <strong>{configForm.custom_domain}</strong>, add these DNS records with your domain provider:
                    </p>
                    <div style={{ fontFamily: 'monospace', fontSize: '13px', backgroundColor: '#fff', padding: '10px', border: '1px solid #dee2e6', borderRadius: '3px' }}>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>A Record:</strong> @ → 66.29.142.152
                      </div>
                      <div style={{ marginBottom: '5px' }}>
                        <strong>CNAME Record:</strong> www → {configForm.custom_domain.replace('www.', '')}
                      </div>
                    </div>
                    <p style={{ margin: '10px 0 0 0', fontSize: '12px', color: '#6c757d' }}>
                      <strong>Note:</strong> DNS changes can take up to 24 hours to propagate. SSL certificate will be automatically generated once DNS is configured.
                    </p>
                  </div>
                )}
              </div>

              <div className="form-group">
                <label>Theme *</label>
                <select
                  value={configForm.theme_id}
                  onChange={(e) => setConfigForm({ ...configForm, theme_id: e.target.value })}
                  required
                >
                  <option value="">Select a theme</option>
                  {availableThemes.map(theme => (
                    <option key={theme.id} value={theme.id}>{theme.name}</option>
                  ))}
                </select>
              </div>
            </div>

            <div className="form-group">
              <label>Meta Title</label>
              <input
                type="text"
                value={configForm.meta_title}
                onChange={(e) => setConfigForm({ ...configForm, meta_title: e.target.value })}
                placeholder="Your Name - Travel Professional"
              />
            </div>

            <div className="form-group">
              <label>Meta Description</label>
              <textarea
                value={configForm.meta_description}
                onChange={(e) => setConfigForm({ ...configForm, meta_description: e.target.value })}
                placeholder="Professional travel services by your name"
                rows={3}
              />
            </div>
          </div>

          <div className="settings-section">
            <h3>Analytics & Tracking</h3>
            <p style={{marginBottom: '1rem', color: '#666', fontSize: '0.9rem'}}>
              Add tracking codes to monitor your website performance and visitor behavior.
            </p>

            {/* Sitemap Link Display */}
            {(websiteConfig?.custom_domain || websiteConfig?.subdomain) && (
              <div style={{
                padding: '1rem',
                backgroundColor: '#f0f9ff',
                border: '1px solid #bae6fd',
                borderRadius: '8px',
                marginBottom: '1.5rem'
              }}>
                <h4 style={{margin: '0 0 0.5rem 0', color: '#0369a1', fontSize: '0.95rem'}}>
                  🗺️ Your Sitemap URL
                </h4>
                <p style={{margin: '0 0 0.5rem 0', fontSize: '0.85rem', color: '#666'}}>
                  Submit this sitemap to Google Search Console to help search engines discover your content:
                </p>
                <div style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.5rem',
                  padding: '0.75rem',
                  backgroundColor: 'white',
                  border: '1px solid #e0e0e0',
                  borderRadius: '4px',
                  fontFamily: 'monospace',
                  fontSize: '0.9rem'
                }}>
                  <code style={{flex: 1, color: '#0369a1'}}>
                    {websiteConfig.custom_domain 
                      ? `https://${websiteConfig.custom_domain}/sitemap.xml`
                      : `https://${websiteConfig.subdomain}.tpcrm.io/sitemap.xml`
                    }
                  </code>
                  <button
                    type="button"
                    onClick={() => {
                      const sitemapUrl = websiteConfig.custom_domain 
                        ? `https://${websiteConfig.custom_domain}/sitemap.xml`
                        : `https://${websiteConfig.subdomain}.tpcrm.io/sitemap.xml`;
                      navigator.clipboard.writeText(sitemapUrl);
                      setSuccess('Sitemap URL copied to clipboard!');
                      setTimeout(() => setSuccess(null), 3000);
                    }}
                    style={{
                      padding: '0.5rem 1rem',
                      backgroundColor: '#0369a1',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.85rem',
                      whiteSpace: 'nowrap'
                    }}
                  >
                    Copy
                  </button>
                </div>
              </div>
            )}
            
            <div className="form-group">
              <label>Google Analytics ID</label>
              <input
                type="text"
                value={configForm.google_analytics_id}
                onChange={(e) => setConfigForm({ ...configForm, google_analytics_id: e.target.value })}
                placeholder="G-XXXXXXXXXX"
              />
              <small>Enter your Google Analytics 4 tracking ID (e.g., G-XXXXXXXXXX)</small>
            </div>

            <div className="form-group">
              <label>Google Search Console Verification Code</label>
              <input
                type="text"
                value={configForm.google_search_console_code}
                onChange={(e) => setConfigForm({ ...configForm, google_search_console_code: e.target.value })}
                placeholder="google-site-verification=..."
              />
              <small>Verification code from Google Search Console (just the code part after "google-site-verification=")</small>
            </div>

            <div className="form-group">
              <label>Facebook Pixel ID</label>
              <input
                type="text"
                value={configForm.facebook_pixel_id}
                onChange={(e) => setConfigForm({ ...configForm, facebook_pixel_id: e.target.value })}
                placeholder="123456789012345"
              />
              <small>For conversion tracking and remarketing (15-16 digit number)</small>
            </div>
          </div>

          <div className="settings-section">
            <h3>Logo</h3>
            <div className="logo-upload">
              {(logoPreview || (websiteConfig && websiteConfig.logo_url)) && (
                <div className="current-logo">
                  <img 
                    src={logoPreview || websiteConfig.logo_url} 
                    alt="Current Logo" 
                    style={{ maxHeight: '100px', maxWidth: '200px' }}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Upload New Logo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setLogoFile(file);
                      setLogoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
                <small>Recommended: PNG or JPG, max 2MB</small>
              </div>
            </div>
          </div>


          <div className="settings-actions">
            {websiteConfig && websiteConfig.subdomain && (
              <div className="website-links">
                <a 
                  href={websiteConfig?.subdomain ? `https://${websiteConfig.subdomain}.tpcrm.io` : '#'} 
                  target="_blank" 
                  rel="noopener noreferrer" 
                  className="btn-view-live"
                >
                  View Live Website
                </a>
              </div>
            )}
            <button 
              className="btn-save-settings"
              onClick={handleSaveSettings}
              disabled={saving || !configForm.subdomain || !configForm.theme_id}
            >
              {saving ? 'Saving...' : 'Save Website Settings'}
            </button>
          </div>
        </div>
      )}

      {/* Website Profile Tab */}
      {activeTab === 'profile' && (
        <div className="website-profile">
          <div className="profile-header">
            <h2>Website Profile</h2>
          </div>

          <div className="profile-section">
            <h3>Profile Photo</h3>
            <div className="profile-photo-upload">
              {(profilePhotoPreview || websiteProfile.photo_url) && (
                <div className="current-profile-photo">
                  <img 
                    src={profilePhotoPreview || websiteProfile.photo_url} 
                    alt="Profile Photo" 
                    style={{ 
                      width: '150px', 
                      height: '150px', 
                      objectFit: 'cover', 
                      borderRadius: '50%',
                      border: '3px solid #e5e7eb'
                    }}
                  />
                </div>
              )}
              <div className="form-group">
                <label>Upload Profile Photo</label>
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => {
                    const file = e.target.files[0];
                    if (file) {
                      setProfilePhotoFile(file);
                      setProfilePhotoPreview(URL.createObjectURL(file));
                    }
                  }}
                />
                <small>Recommended: Square image, 400x400px minimum, max 5MB</small>
              </div>
            </div>
          </div>

          <div className="profile-section">
            <h3>Public Profile Information</h3>
            <div className="form-group">
              <label>Display Name</label>
              <input
                type="text"
                value={websiteProfile.display_name}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, display_name: e.target.value })}
                placeholder="Your Name"
              />
            </div>

            <div className="form-group">
              <label>Agency Name</label>
              <input
                type="text"
                value={websiteProfile.agency_name}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, agency_name: e.target.value })}
                placeholder="Your Agency Name"
              />
            </div>

            <div className="form-group">
              <label>Public Email</label>
              <input
                type="email"
                value={websiteProfile.public_email}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, public_email: e.target.value })}
                placeholder="Your Public Email Address"
              />
            </div>

            <div className="form-group">
              <label>Public Phone</label>
              <input
                type="text"
                value={websiteProfile.public_phone}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, public_phone: e.target.value })}
                placeholder="Your Public Phone Number"
              />
            </div>
          </div>

          <div className="profile-section">
            <h3>Agent Information</h3>
            <div className="form-group">
              <label>Title</label>
              <input
                type="text"
                value={websiteProfile.title}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, title: e.target.value })}
                placeholder="Your Title"
              />
            </div>

            <div className="form-group">
              <label>About Me Bio</label>
              <textarea
                value={websiteProfile.bio}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, bio: e.target.value })}
                placeholder="Tell your story! Share your passion for travel, what makes you unique as a travel advisor, and why clients should choose you. This will be prominently displayed on your About Me page."
                rows={6}
              />
              <small>This bio will be the main introduction on your About Me page. Make it personal and engaging!</small>
            </div>

            <div className="form-group">
              <label>Years of Experience</label>
              <input
                type="number"
                value={websiteProfile.years_experience}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, years_experience: e.target.value })}
                placeholder="Your Years of Experience"
              />
            </div>

            <div className="form-group">
              <label>Travel Specialties</label>
              <input
                type="text"
                value={websiteProfile.specialties}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, specialties: e.target.value })}
                placeholder="e.g., Disney Vacations, Luxury Cruises, European Tours, Honeymoons, Family Travel"
              />
              <small>Separate multiple specialties with commas. These will be displayed as a list on your About Me page.</small>
            </div>
          </div>

          <div className="profile-section">
            <h3>Agency Information</h3>
            <div className="form-group">
              <label>Agency Address</label>
              <textarea
                value={websiteProfile.agency_address}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, agency_address: e.target.value })}
                placeholder="Your Agency Address"
                rows={3}
              />
            </div>

            <div className="form-group">
              <label>Agency Website</label>
              <input
                type="url"
                value={websiteProfile.agency_website}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, agency_website: e.target.value })}
                placeholder="Your Agency Website"
              />
            </div>
          </div>

          <div className="profile-section">
            <h3>Social Media Links</h3>
            <div className="form-group">
              <label>LinkedIn URL</label>
              <input
                type="url"
                value={websiteProfile.linkedin_url}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, linkedin_url: e.target.value })}
                placeholder="Your LinkedIn URL"
              />
            </div>

            <div className="form-group">
              <label>Facebook URL</label>
              <input
                type="url"
                value={websiteProfile.facebook_url}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, facebook_url: e.target.value })}
                placeholder="Your Facebook URL"
              />
            </div>

            <div className="form-group">
              <label>Instagram URL</label>
              <input
                type="url"
                value={websiteProfile.instagram_url}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, instagram_url: e.target.value })}
                placeholder="Your Instagram URL"
              />
            </div>

            <div className="form-group">
              <label>TikTok URL</label>
              <input
                type="url"
                value={websiteProfile.tiktok_url}
                onChange={(e) => setWebsiteProfile({ ...websiteProfile, tiktok_url: e.target.value })}
                placeholder="Your TikTok URL"
              />
            </div>
          </div>

          {/* Services Management Section */}
          <div className="profile-section">
            <h3>Services Management</h3>
            <p className="section-description">
              Manage the services displayed on your Services page. Add, edit, or remove services to showcase what you offer to clients.
            </p>
            
            {/* Services Description */}
            <div className="form-group">
              <label>Services Overview Description</label>
              <textarea
                value={servicesDescription}
                onChange={(e) => setServicesDescription(e.target.value)}
                placeholder="Describe your travel services and expertise. This will appear at the top of your Services page to introduce visitors to what you offer."
                rows={4}
              />
              <small>This description appears at the top of your Services page and helps visitors understand your expertise.</small>
              <button 
                type="button"
                className="btn-save-description"
                onClick={handleSaveServicesDescription}
                disabled={servicesSaving}
                style={{ marginTop: '0.5rem' }}
              >
                {servicesSaving ? 'Saving...' : 'Save Description'}
              </button>
            </div>

            {/* Add New Service */}
            <div className="add-service-section">
              <h4>Add New Service</h4>
              <div className="service-form">
                <div className="form-group">
                  <label>Service Name</label>
                  <input
                    type="text"
                    value={newService.serviceName}
                    onChange={(e) => setNewService({ ...newService, serviceName: e.target.value })}
                    placeholder="e.g., Disney World Vacation Planning"
                  />
                </div>
                <div className="form-group">
                  <label>Service Description</label>
                  <textarea
                    value={newService.serviceDescription}
                    onChange={(e) => setNewService({ ...newService, serviceDescription: e.target.value })}
                    placeholder="Describe what this service includes and how it benefits your clients"
                    rows={3}
                  />
                </div>
                <button 
                  type="button"
                  className="btn-add-service"
                  onClick={handleAddService}
                  disabled={servicesSaving}
                >
                  {servicesSaving ? 'Adding...' : 'Add Service'}
                </button>
              </div>
            </div>

            {/* Current Services List */}
            <div className="services-list-section">
              <h4>Current Services ({services.length})</h4>
              {services.length === 0 ? (
                <p className="no-services">No services added yet. Add your first service above to get started!</p>
              ) : (
                <div className="services-list">
                  {services.map((service) => (
                    <div key={service.id} className="service-item">
                      <div className="service-content">
                        <h5>{service.service_name}</h5>
                        <p>{service.service_description || 'No description provided'}</p>
                      </div>
                      <div className="service-actions">
                        <button 
                          type="button"
                          className="btn-edit-service"
                          onClick={() => handleEditService(service)}
                        >
                          Edit
                        </button>
                        <button 
                          type="button"
                          className="btn-delete-service"
                          onClick={() => handleDeleteService(service.id)}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="profile-actions">
            <button 
              className="btn-save-profile"
              onClick={handleSaveProfile}
              disabled={profileSaving}
            >
              {profileSaving ? 'Saving...' : 'Save Website Profile'}
            </button>
          </div>
        </div>
      )}

      {/* Service Edit Modal */}
      {showServiceModal && (
        <div className="modal-overlay">
          <div className="modal-content">
            <div className="modal-header">
              <h3>{editingService ? 'Edit Service' : 'Add Service'}</h3>
              <button 
                className="modal-close"
                onClick={() => {
                  setShowServiceModal(false);
                  setEditingService(null);
                  setNewService({ serviceName: '', serviceDescription: '' });
                }}
              >
                ×
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Service Name</label>
                <input
                  type="text"
                  value={newService.serviceName}
                  onChange={(e) => setNewService({ ...newService, serviceName: e.target.value })}
                  placeholder="e.g., Disney World Vacation Planning"
                />
              </div>
              <div className="form-group">
                <label>Service Description</label>
                <textarea
                  value={newService.serviceDescription}
                  onChange={(e) => setNewService({ ...newService, serviceDescription: e.target.value })}
                  placeholder="Describe what this service includes and how it benefits your clients"
                  rows={4}
                />
              </div>
            </div>
            <div className="modal-actions">
              <button 
                type="button"
                className="btn-cancel"
                onClick={() => {
                  setShowServiceModal(false);
                  setEditingService(null);
                  setNewService({ serviceName: '', serviceDescription: '' });
                }}
              >
                Cancel
              </button>
              <button 
                type="button"
                className="btn-primary"
                onClick={editingService ? handleUpdateService : handleAddService}
                disabled={servicesSaving}
              >
                {servicesSaving ? 'Saving...' : (editingService ? 'Update Service' : 'Add Service')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Content Cards Tab */}
      {activeTab === 'promotions' && (
        <div className="promotions-management">
          <div className="promotions-header">
            <h2>Content Cards</h2>
            <p className="section-description">Create custom content cards for your website</p>
            <button 
              className="btn-new-promotion"
              onClick={() => {
                setEditingPromotion(null);
                setShowPromotionModal(true);
              }}
            >
              <svg className="w-5 h-5 mr-2" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              New Content Card
            </button>
          </div>

          <div className="promotions-grid">
            {promotions.length === 0 ? (
              <div className="empty-state">
                <svg className="empty-icon" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                <h3>No content cards yet</h3>
                <p>Create your first content card to showcase your travel offerings</p>
              </div>
            ) : (
              promotions.map(contentCard => (
                <div key={contentCard.id} className="promotion-card">
                  <div className="promotion-image">
                    {contentCard.main_photo && (
                      <img src={contentCard.main_photo} alt={contentCard.title} />
                    )}
                  </div>
                  <div className="promotion-content">
                    <h3>{contentCard.title}</h3>
                    <p className="promotion-excerpt">{contentCard.short_description}</p>
                    <div className="promotion-meta">
                      <span className="promotion-supplier">
                        {contentCard.supplier_name || contentCard.supplier || 'No Supplier'}
                      </span>
                      <span className="promotion-categories">
                        {contentCard.categories?.map(cat => cat.name).join(', ') || 'No Categories'}
                      </span>
                    </div>
                    <div className="promotion-status">
                      <span className={`status-badge ${contentCard.status === 'active' ? 'active' : 'inactive'}`}>
                        {contentCard.status === 'active' ? 'Active' : 'Inactive'}
                      </span>
                      {contentCard.price_from && (
                        <span className="price-badge">From ${contentCard.price_from}</span>
                      )}
                    </div>
                    <div className="promotion-actions">
                      <button 
                        className="btn-edit"
                        onClick={() => {
                          setEditingPromotion(contentCard);
                          setShowPromotionModal(true);
                        }}
                      >
                        Edit
                      </button>
                      <button 
                        className={`btn-toggle ${contentCard.status === 'active' ? 'active' : 'inactive'}`}
                        onClick={() => handleTogglePromotion(contentCard.id, contentCard.status !== 'active')}
                      >
                        {contentCard.status === 'active' ? 'Deactivate' : 'Activate'}
                      </button>
                      <button 
                        className="btn-delete"
                        onClick={() => handleDeletePromotion(contentCard.id)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </div>
      )}

      {/* Submissions Tab */}
      {activeTab === 'submissions' && (
        <div className="submissions-section">
          <div className="section-header">
            <h2>Website Contact & Quote Submissions</h2>
            <p>Manage inquiries and quote requests from your website</p>
          </div>

          {/* Filters */}
          <div className="submissions-filters">
            <div className="filter-group">
              <label>Status:</label>
              <select 
                value={submissionFilters.status} 
                onChange={(e) => setSubmissionFilters({...submissionFilters, status: e.target.value})}
              >
                <option value="all">All Statuses</option>
                <option value="new">New</option>
                <option value="read">Read</option>
                <option value="replied">Replied</option>
              </select>
            </div>
            
            <div className="filter-group">
              <label>Type:</label>
              <select 
                value={submissionFilters.type} 
                onChange={(e) => setSubmissionFilters({...submissionFilters, type: e.target.value})}
              >
                <option value="all">All Types</option>
                <option value="quote">Quote Requests</option>
                <option value="general">General Contact</option>
              </select>
            </div>
          </div>

          {/* Submissions List */}
          <div className="submissions-list">
            {submissionsLoading ? (
              <div className="loading-state">
                <div className="spinner"></div>
                <p>Loading submissions...</p>
              </div>
            ) : submissions.length === 0 ? (
              <div className="empty-state">
                <div className="empty-icon">📭</div>
                <h4>No submissions yet</h4>
                <p>Contact forms and quote requests from your website will appear here.</p>
              </div>
            ) : (
              <div className="submissions-table">
                <div className="table-header">
                  <div className="col-type">Type</div>
                  <div className="col-name">Name</div>
                  <div className="col-email">Email</div>
                  <div className="col-date">Date</div>
                  <div className="col-status">Status</div>
                  <div className="col-actions">Actions</div>
                </div>
                
                {submissions.map(submission => (
                  <SubmissionRow 
                    key={submission.id} 
                    submission={submission}
                    onStatusUpdate={handleStatusUpdate}
                    onViewDetails={handleViewDetails}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Blog Modal */}
      {showBlogModal && (
        <BlogModal
          blog={editingBlog}
          onClose={() => {
            setShowBlogModal(false);
            setEditingBlog(null);
          }}
          onSave={handleBlogSubmit}
        />
      )}

      {/* Content Card Modal */}
      {showPromotionModal && (
        <ContentCardModal
          contentCard={editingPromotion}
          onClose={() => {
            setShowPromotionModal(false);
            setEditingPromotion(null);
          }}
          onSave={handleSavePromotion}
        />
      )}
    </div>
  );
};

// Submission Row Component
const SubmissionRow = ({ submission, onStatusUpdate, onViewDetails }) => {
  const getStatusBadge = (status) => {
    const statusConfig = {
      new: { color: 'blue', label: 'New' },
      read: { color: 'yellow', label: 'Read' },
      replied: { color: 'green', label: 'Replied' }
    };
    
    const config = statusConfig[status] || statusConfig.new;
    return (
      <span className={`status-badge status-${config.color}`}>
        {config.label}
      </span>
    );
  };

  const getTypeIcon = (purpose) => {
    return purpose === 'quote' ? '💼' : '📧';
  };

  return (
    <div className="submission-row">
      <div className="col-type">
        <span className="type-icon">{getTypeIcon(submission.purpose)}</span>
        <span className="type-label">
          {submission.purpose === 'quote' ? 'Quote Request' : 'Contact'}
        </span>
      </div>
      
      <div className="col-name">
        <strong>{submission.name}</strong>
      </div>
      
      <div className="col-email">
        <a href={`mailto:${submission.email}`}>{submission.email}</a>
      </div>
      
      <div className="col-date">
        {new Date(submission.submitted_at).toLocaleDateString()}
      </div>
      
      <div className="col-status">
        {getStatusBadge(submission.status)}
      </div>
      
      <div className="col-actions">
        <button 
          className="btn-secondary btn-sm"
          onClick={() => onViewDetails(submission)}
        >
          View
        </button>
        
        <select 
          value={submission.status}
          onChange={(e) => onStatusUpdate(submission.id, e.target.value)}
          className="status-select"
        >
          <option value="new">New</option>
          <option value="read">Read</option>
          <option value="replied">Replied</option>
        </select>
      </div>
    </div>
  );
};

// Blog Modal Component
const BlogModal = ({ blog, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: blog?.title || '',
    content: blog?.content || '',
    category: blog?.category || 'Travel Tips',
    excerpt: blog?.excerpt || '',
    status: blog?.status || 'published',
    slug: blog?.slug || '',
    meta_description: blog?.meta_description || '',
    focus_keyword: blog?.focus_keyword || '',
    alt_text: blog?.alt_text || '',
    file_ids: []
  });
  const [uploadedFiles, setUploadedFiles] = useState(blog?.files || []);
  const [uploading, setUploading] = useState(false);

  const blogCategories = [
    'Travel Tips', 'Destination Guides', 'Cruise News',
    'Theme Parks', 'Travel Deals', 'Client Stories',
    'Travel Insurance', 'Packing Tips', 'Travel Safety'
  ];

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files);
    if (files.length === 0) return;

    setUploading(true);
    try {
      const uploadPromises = files.map(file => uploadService.uploadFile(file, 'content'));
      const results = await Promise.all(uploadPromises);
      
      // Backend returns { files: [...] }, so we need to extract the files array
      const newFiles = results.flatMap(result => result.files || []);
      setUploadedFiles([...uploadedFiles, ...newFiles]);
      setFormData({
        ...formData,
        file_ids: [...formData.file_ids, ...newFiles.map(f => f.id)]
      });
    } catch (error) {
      console.error('Error uploading files:', error);
      alert('Failed to upload files');
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validate required fields
    if (!formData.title.trim()) {
      alert('Please enter a title for your blog post.');
      return;
    }
    
    if (!formData.content.trim() || formData.content === '<p><br></p>') {
      alert('Please enter content for your blog post.');
      return;
    }
    
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content blog-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{blog ? 'Edit Blog Post' : 'New Blog Post'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="blog-form">
          <div className="form-group">
            <label>Title *</label>
            <input
              type="text"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              required
              placeholder="Enter blog post title"
            />
          </div>

          <div className="form-row">
            <div className="form-group">
              <label>Category</label>
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
              >
                {blogCategories.map(cat => (
                  <option key={cat} value={cat}>{cat}</option>
                ))}
              </select>
            </div>

            <div className="form-group">
              <label>Status</label>
              <select
                value={formData.status}
                onChange={(e) => setFormData({ ...formData, status: e.target.value })}
              >
                <option value="draft">Draft</option>
                <option value="published">Published</option>
              </select>
            </div>
          </div>

          <div className="form-group">
            <label>Excerpt (optional)</label>
            <textarea
              value={formData.excerpt}
              onChange={(e) => setFormData({ ...formData, excerpt: e.target.value })}
              rows={2}
              placeholder="Brief description of the blog post"
            />
          </div>

          <div className="form-group">
            <label>Content *</label>
            <RichTextEditor
              value={formData.content}
              onChange={(value) => setFormData({ ...formData, content: value })}
              placeholder="Write your blog post content here..."
              height="400px"
            />
          </div>

          {/* SEO Fields Section */}
          <div style={{marginTop: '2rem', padding: '1.5rem', backgroundColor: '#f8f9fa', borderRadius: '8px'}}>
            <h3 style={{marginTop: 0, marginBottom: '1rem', fontSize: '1.1rem', color: '#333'}}>
              🔍 SEO Optimization
            </h3>
            
            <div className="form-group">
              <label>URL Slug (SEO-friendly URL)</label>
              <input
                type="text"
                value={formData.slug}
                onChange={(e) => setFormData({ ...formData, slug: e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-') })}
                placeholder="best-caribbean-cruises-2026"
              />
              <small>Leave blank to auto-generate from title. Use lowercase letters, numbers, and hyphens only.</small>
            </div>

            <div className="form-group">
              <label>Meta Description ({formData.meta_description.length}/160 characters)</label>
              <textarea
                maxLength="160"
                value={formData.meta_description}
                onChange={(e) => setFormData({ ...formData, meta_description: e.target.value })}
                rows={2}
                placeholder="Brief description for search results (160 characters max)"
              />
              <small>This appears in search engine results. Keep it compelling and under 160 characters.</small>
            </div>

            <div className="form-group">
              <label>Focus Keyword</label>
              <input
                type="text"
                value={formData.focus_keyword}
                onChange={(e) => setFormData({ ...formData, focus_keyword: e.target.value })}
                placeholder="caribbean cruises"
              />
              <small>The main keyword you want this post to rank for in search engines.</small>
            </div>

            <div className="form-group">
              <label>Featured Image Alt Text</label>
              <input
                type="text"
                value={formData.alt_text}
                onChange={(e) => setFormData({ ...formData, alt_text: e.target.value })}
                placeholder="Description of the featured image"
              />
              <small>Describes the image for accessibility and SEO. Be descriptive and include keywords.</small>
            </div>
          </div>

          <div className="form-group">
            <label>Images</label>
            <div className="file-upload-area">
              <input
                type="file"
                multiple
                accept="image/*"
                onChange={handleFileUpload}
                disabled={uploading}
                id="blog-images"
              />
              <label htmlFor="blog-images" className="file-upload-label">
                {uploading ? 'Uploading...' : 'Choose Images'}
              </label>
              
              {uploadedFiles.length > 0 && (
                <div className="uploaded-files">
                  {uploadedFiles.map((file, index) => (
                    <div key={index} className="uploaded-file">
                      <img 
                        src={file.url || file.path || `/uploads/${file.filename}`} 
                        alt={file.original_name} 
                      />
                      <button
                        type="button"
                        onClick={() => {
                          setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
                          setFormData({
                            ...formData,
                            file_ids: formData.file_ids.filter(id => id !== file.id)
                          });
                        }}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {blog ? 'Update Post' : 'Create Post'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Content Card Modal Component
const ContentCardModal = ({ contentCard, onClose, onSave }) => {
  const [formData, setFormData] = useState({
    title: contentCard?.title || '',
    short_description: contentCard?.short_description || '',
    offer_details: contentCard?.offer_details || '',
    main_photo: contentCard?.main_photo || '',
    supplier: contentCard?.supplier || '',
    supplier_id: contentCard?.supplier_id || '',
    category_ids: contentCard?.categories?.map(cat => cat.id) || [],
    start_date: contentCard?.start_date ? contentCard.start_date.split('T')[0] : '',
    end_date: contentCard?.end_date ? contentCard.end_date.split('T')[0] : '',
    price_from: contentCard?.price_from || '',
    duration: contentCard?.duration || '',
    highlights: contentCard?.highlights || [],
    included: contentCard?.included || [],
    not_included: contentCard?.not_included || [],
    booking_notes: contentCard?.booking_notes || '',
    status: contentCard?.status || 'active'
  });
  const [uploading, setUploading] = useState(false);
  const [suppliers, setSuppliers] = useState([]);
  const [categories, setCategories] = useState([]);
  const [newHighlight, setNewHighlight] = useState('');
  const [newIncluded, setNewIncluded] = useState('');
  const [newNotIncluded, setNewNotIncluded] = useState('');

  useEffect(() => {
    loadSuppliers();
    loadCategories();
  }, []);

  const loadSuppliers = async () => {
    try {
      const suppliersData = await promotionsService.getSuppliers();
      setSuppliers(suppliersData);
    } catch (error) {
      console.error('Error loading suppliers:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const categoriesData = await promotionsService.getCategories();
      setCategories(categoriesData);
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const handleImageUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    try {
      const result = await uploadService.uploadFile(file, 'content-cards');
      const uploadedFile = result.files?.[0];
      if (uploadedFile) {
        setFormData({
          ...formData,
          main_photo: uploadedFile.url || uploadedFile.path || `/uploads/${uploadedFile.filename}`
        });
      }
    } catch (error) {
      console.error('Error uploading image:', error);
      alert('Failed to upload image');
    } finally {
      setUploading(false);
    }
  };

  const handleCategoryToggle = (categoryId) => {
    const currentIds = formData.category_ids;
    if (currentIds.includes(categoryId)) {
      setFormData({
        ...formData,
        category_ids: currentIds.filter(id => id !== categoryId)
      });
    } else {
      setFormData({
        ...formData,
        category_ids: [...currentIds, categoryId]
      });
    }
  };

  const addHighlight = () => {
    if (newHighlight.trim()) {
      setFormData({
        ...formData,
        highlights: [...formData.highlights, newHighlight.trim()]
      });
      setNewHighlight('');
    }
  };

  const removeHighlight = (index) => {
    setFormData({
      ...formData,
      highlights: formData.highlights.filter((_, i) => i !== index)
    });
  };

  const addIncluded = () => {
    if (newIncluded.trim()) {
      setFormData({
        ...formData,
        included: [...formData.included, newIncluded.trim()]
      });
      setNewIncluded('');
    }
  };

  const removeIncluded = (index) => {
    setFormData({
      ...formData,
      included: formData.included.filter((_, i) => i !== index)
    });
  };

  const addNotIncluded = () => {
    if (newNotIncluded.trim()) {
      setFormData({
        ...formData,
        not_included: [...formData.not_included, newNotIncluded.trim()]
      });
      setNewNotIncluded('');
    }
  };

  const removeNotIncluded = (index) => {
    setFormData({
      ...formData,
      not_included: formData.not_included.filter((_, i) => i !== index)
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave(formData);
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-content content-card-modal" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <h2>{contentCard ? 'Edit Content Card' : 'New Content Card'}</h2>
          <button className="modal-close" onClick={onClose}>×</button>
        </div>
        
        <form onSubmit={handleSubmit} className="content-card-form">
          {/* Basic Information */}
          <div className="form-section">
            <h3>Basic Information</h3>
            
            <div className="form-group">
              <label>Title *</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                required
                placeholder="Enter content card title"
              />
            </div>

            <div className="form-group">
              <label>Short Description *</label>
              <textarea
                value={formData.short_description}
                onChange={(e) => setFormData({ ...formData, short_description: e.target.value })}
                rows={2}
                required
                placeholder="Brief description for card preview"
              />
            </div>

            <div className="form-group">
              <label>Detailed Offer Information *</label>
              <textarea
                value={formData.offer_details}
                onChange={(e) => setFormData({ ...formData, offer_details: e.target.value })}
                rows={4}
                required
                placeholder="Complete offer details and description"
              />
            </div>
          </div>

          {/* Supplier Information */}
          <div className="form-section">
            <h3>Supplier Information</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Supplier</label>
                <select
                  value={formData.supplier_id}
                  onChange={(e) => {
                    const selectedSupplier = suppliers.find(s => s.id === parseInt(e.target.value));
                    setFormData({
                      ...formData,
                      supplier_id: e.target.value,
                      supplier: selectedSupplier?.name || ''
                    });
                  }}
                >
                  <option value="">Select a supplier</option>
                  {suppliers.map(supplier => (
                    <option key={supplier.id} value={supplier.id}>
                      {supplier.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Custom Supplier Name</label>
                <input
                  type="text"
                  value={formData.supplier}
                  onChange={(e) => setFormData({ ...formData, supplier: e.target.value })}
                  placeholder="Or enter custom supplier name"
                />
              </div>
            </div>
          </div>

          {/* Categories */}
          <div className="form-section">
            <h3>Categories</h3>
            <p className="form-help">Select categories that apply to this content card</p>
            
            <div className="categories-grid">
              {categories.map(category => (
                <label key={category.id} className="category-checkbox">
                  <input
                    type="checkbox"
                    checked={formData.category_ids.includes(category.id)}
                    onChange={() => handleCategoryToggle(category.id)}
                  />
                  <span className="category-name">{category.name}</span>
                </label>
              ))}
            </div>
          </div>

          {/* Dates and Pricing */}
          <div className="form-section">
            <h3>Dates and Pricing</h3>
            
            <div className="form-row">
              <div className="form-group">
                <label>Start Date</label>
                <input
                  type="date"
                  value={formData.start_date}
                  onChange={(e) => setFormData({ ...formData, start_date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>End Date</label>
                <input
                  type="date"
                  value={formData.end_date}
                  onChange={(e) => setFormData({ ...formData, end_date: e.target.value })}
                />
              </div>

              <div className="form-group">
                <label>Price From</label>
                <input
                  type="number"
                  value={formData.price_from}
                  onChange={(e) => setFormData({ ...formData, price_from: e.target.value })}
                  placeholder="Starting price"
                  step="0.01"
                />
              </div>

              <div className="form-group">
                <label>Duration</label>
                <input
                  type="text"
                  value={formData.duration}
                  onChange={(e) => setFormData({ ...formData, duration: e.target.value })}
                  placeholder="e.g., 7 nights, 3 days"
                />
              </div>
            </div>
          </div>

          {/* Image Upload */}
          <div className="form-section">
            <h3>Main Image</h3>
            
            <div className="file-upload-area">
              <input
                type="file"
                accept="image/*"
                onChange={handleImageUpload}
                disabled={uploading}
                id="content-card-image"
              />
              <label htmlFor="content-card-image" className="file-upload-label">
                {uploading ? 'Uploading...' : 'Choose Image'}
              </label>
              
              {formData.main_photo && (
                <div className="image-preview">
                  <img src={formData.main_photo} alt="Content card preview" />
                  <button
                    type="button"
                    onClick={() => setFormData({ ...formData, main_photo: '' })}
                  >
                    Remove
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* Highlights */}
          <div className="form-section">
            <h3>Highlights</h3>
            <p className="form-help">Key selling points and features</p>
            
            <div className="list-input">
              <div className="add-item">
                <input
                  type="text"
                  value={newHighlight}
                  onChange={(e) => setNewHighlight(e.target.value)}
                  placeholder="Add a highlight"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addHighlight())}
                />
                <button type="button" onClick={addHighlight}>Add</button>
              </div>
              
              <div className="items-list">
                {formData.highlights.map((highlight, index) => (
                  <div key={index} className="list-item">
                    <span>{highlight}</span>
                    <button type="button" onClick={() => removeHighlight(index)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Included */}
          <div className="form-section">
            <h3>What's Included</h3>
            
            <div className="list-input">
              <div className="add-item">
                <input
                  type="text"
                  value={newIncluded}
                  onChange={(e) => setNewIncluded(e.target.value)}
                  placeholder="Add included item"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addIncluded())}
                />
                <button type="button" onClick={addIncluded}>Add</button>
              </div>
              
              <div className="items-list">
                {formData.included.map((item, index) => (
                  <div key={index} className="list-item">
                    <span>{item}</span>
                    <button type="button" onClick={() => removeIncluded(index)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Not Included */}
          <div className="form-section">
            <h3>What's Not Included</h3>
            
            <div className="list-input">
              <div className="add-item">
                <input
                  type="text"
                  value={newNotIncluded}
                  onChange={(e) => setNewNotIncluded(e.target.value)}
                  placeholder="Add not included item"
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addNotIncluded())}
                />
                <button type="button" onClick={addNotIncluded}>Add</button>
              </div>
              
              <div className="items-list">
                {formData.not_included.map((item, index) => (
                  <div key={index} className="list-item">
                    <span>{item}</span>
                    <button type="button" onClick={() => removeNotIncluded(index)}>×</button>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Booking Notes */}
          <div className="form-section">
            <h3>Booking Notes</h3>
            
            <div className="form-group">
              <label>Additional Information</label>
              <textarea
                value={formData.booking_notes}
                onChange={(e) => setFormData({ ...formData, booking_notes: e.target.value })}
                rows={3}
                placeholder="Special instructions, terms, or additional details"
              />
            </div>
          </div>

          <div className="modal-actions">
            <button type="button" className="btn-cancel" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="btn-primary">
              {contentCard ? 'Update Content Card' : 'Create Content Card'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Export WebsiteManagement directly (subscription check now handled by WebsiteWrapper)
export default WebsiteManagement;
